import React, { useMemo, useState } from "react";
import { PencilSimple, Plus, X, Copy, DotsSixVertical } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { getBarSeconds, getBarCount } from "@/lib/audio";

export default function BarEditor({ track, bpm, busy, onClose, onApply }) {
  // Build initial plan from the original track: one "bar" entry per natural bar.
  const initialPlan = useMemo(() => {
    const total = getBarCount(track?.duration || 0, bpm);
    return Array.from({ length: total }, (_, i) => ({ type: "bar", sourceIndex: i }));
  }, [track, bpm]);
  const [plan, setPlan] = useState(initialPlan);
  const [dragFrom, setDragFrom] = useState(null); // index of bar being dragged
  const [dragOver, setDragOver] = useState(null); // index currently hovered

  function removeAt(i) {
    setPlan((p) => p.filter((_, idx) => idx !== i));
  }
  function insertSilenceAt(i) {
    setPlan((p) => [...p.slice(0, i), { type: "silence" }, ...p.slice(i)]);
  }
  // Duplicate the chip at index `i` — inserts an identical entry right after it.
  // For real bars this references the same sourceIndex (so the audio replays the
  // same source bar twice); for silence it inserts another silent gap.
  function duplicateAt(i) {
    setPlan((p) => [...p.slice(0, i + 1), { ...p[i] }, ...p.slice(i + 1)]);
  }
  // Move entry from `from` to `to` (after-removal index space). Used by drag-drop.
  function moveEntry(from, to) {
    if (from === to) return;
    setPlan((p) => {
      const next = p.slice();
      const [moved] = next.splice(from, 1);
      // After removal, shift target down by one if it was to the right of `from`.
      const insertAt = to > from ? to - 1 : to;
      next.splice(insertAt, 0, moved);
      return next;
    });
  }
  function reset() { setPlan(initialPlan); setDragFrom(null); setDragOver(null); }

  const barSec = getBarSeconds(bpm);
  const originalBarCount = initialPlan.length;
  const keptBars = plan.filter((e) => e.type === "bar").length;
  const silenceBars = plan.filter((e) => e.type === "silence").length;

  return (
    <Dialog open={true} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent
        className="bg-[#1A1B1D] border border-[#34373B] rounded-3xl text-white max-w-3xl"
        data-testid="bar-editor-dialog"
      >
        <DialogHeader>
          <DialogTitle className="font-display text-3xl tracking-tighter flex items-center gap-2">
            <PencilSimple size={22} weight="bold" /> Edit bars · {track.name}
          </DialogTitle>
          <DialogDescription className="text-[#9CA0A6] text-sm">
            {bpm} BPM · {barSec.toFixed(2)}s per bar · <span className="text-[#E11D48]">×</span> delete, <span className="text-[#60A5FA]">⧉</span> copy, <span className="text-[#A8FF1F]">+</span> insert silence, drag the grip to reorder. Applying creates a new sibling track — your original recording is untouched.
          </DialogDescription>
        </DialogHeader>
        <div className="max-h-[55vh] overflow-y-auto py-3" data-testid="bar-editor-grid">
          <div className="flex flex-wrap items-center gap-1.5">
            <InsertSlot onClick={() => insertSilenceAt(0)} testid="bar-insert-0" />
            {plan.map((entry, i) => (
              <React.Fragment key={`be-${i}-${entry.type}-${entry.sourceIndex ?? "s"}`}>
                <BarChip
                  entry={entry}
                  index={i}
                  total={plan.length}
                  onRemove={() => removeAt(i)}
                  onDuplicate={() => duplicateAt(i)}
                  isDragSource={dragFrom === i}
                  isDragTarget={dragOver === i}
                  onDragStart={() => setDragFrom(i)}
                  onDragOver={(e) => { e.preventDefault(); setDragOver(i); }}
                  onDragLeave={() => setDragOver((d) => (d === i ? null : d))}
                  onDrop={() => {
                    if (dragFrom !== null && dragFrom !== i) moveEntry(dragFrom, i);
                    setDragFrom(null); setDragOver(null);
                  }}
                  onDragEnd={() => { setDragFrom(null); setDragOver(null); }}
                />
                <InsertSlot onClick={() => insertSilenceAt(i + 1)} testid={`bar-insert-${i + 1}`} />
              </React.Fragment>
            ))}
          </div>
        </div>
        <div className="flex items-center justify-between text-xs font-mono uppercase tracking-widest text-[#9CA0A6]">
          <div>
            Original: <span className="text-white">{originalBarCount}</span> ·{" "}
            Kept: <span className="text-[#A8FF1F]">{keptBars}</span> ·{" "}
            Inserted silence: <span className="text-[#FBBF24]">{silenceBars}</span>
          </div>
          <button
            type="button"
            onClick={reset}
            data-testid="bar-editor-reset"
            className="text-[#9CA0A6] hover:text-white"
          >
            Reset
          </button>
        </div>
        <DialogFooter className="flex gap-2">
          <Button
            onClick={onClose}
            data-testid="bar-editor-cancel"
            className="bb-pill bg-[#232427] hover:bg-[#2D2F33] text-white"
          >
            Cancel
          </Button>
          <Button
            onClick={() => onApply(plan)}
            disabled={busy || plan.length === 0}
            data-testid="bar-editor-apply"
            className="bb-pill bg-[#A8FF1F] hover:bg-[#94E600] text-[#0A0A0A] font-semibold"
          >
            {busy ? "Applying…" : "Apply & save as sibling"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function BarChip({
  entry, index, total, onRemove, onDuplicate,
  isDragSource, isDragTarget,
  onDragStart, onDragOver, onDragLeave, onDrop, onDragEnd,
}) {
  const isSilence = entry.type === "silence";
  const label = isSilence ? "rest" : `bar ${entry.sourceIndex + 1}`;
  return (
    <div
      draggable
      onDragStart={onDragStart}
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
      onDragEnd={onDragEnd}
      data-testid={`bar-chip-${index}`}
      className={`relative group inline-flex items-center justify-center w-20 h-16 rounded-xl border font-mono text-xs uppercase tracking-widest cursor-grab active:cursor-grabbing transition-all ${
        isSilence
          ? "border-[#FBBF24]/60 bg-[#FBBF24]/10 text-[#FBBF24]"
          : "border-[#34373B] bg-[#232427] text-white"
      } ${isDragSource ? "opacity-50 scale-95" : ""} ${
        isDragTarget && !isDragSource ? "ring-2 ring-[#A8FF1F] -translate-y-0.5" : ""
      }`}
    >
      {/* Drag grip — visible cue that the chip is movable */}
      <div
        className="absolute left-1 top-1/2 -translate-y-1/2 text-[#5A5E63] group-hover:text-[#9CA0A6] pointer-events-none"
        data-testid={`bar-grip-${index}`}
      >
        <DotsSixVertical size={12} weight="bold" />
      </div>
      <div className="text-center leading-tight pl-3">
        <div className="text-[10px] text-[#9CA0A6]">#{index + 1}</div>
        <div className="mt-1">{label}</div>
      </div>
      {/* Duplicate */}
      <button
        type="button"
        onClick={onDuplicate}
        data-testid={`bar-duplicate-${index}`}
        className="absolute -top-2 -left-2 w-5 h-5 rounded-full bg-[#60A5FA] text-white text-[10px] inline-flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
        aria-label="Duplicate bar"
        title="Copy bar"
      >
        <Copy size={10} weight="bold" />
      </button>
      {/* Delete */}
      {total > 1 && (
        <button
          type="button"
          onClick={onRemove}
          data-testid={`bar-remove-${index}`}
          className="absolute -top-2 -right-2 w-5 h-5 rounded-full bg-[#E11D48] text-white text-[10px] inline-flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
          aria-label="Delete bar"
          title="Delete bar"
        >
          <X size={10} weight="bold" />
        </button>
      )}
    </div>
  );
}

function InsertSlot({ onClick, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      aria-label="Insert silent bar"
      className="group w-3 h-16 inline-flex items-center justify-center"
    >
      <span className="w-1 h-full bg-transparent group-hover:bg-[#A8FF1F]/40 rounded-full transition-colors relative">
        <span className="absolute -left-2 top-1/2 -translate-y-1/2 w-5 h-5 rounded-full bg-[#A8FF1F] text-[#0A0A0A] text-[10px] inline-flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
          <Plus size={10} weight="bold" />
        </span>
      </span>
    </button>
  );
}
